import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AutoComplete, Input, Spin, Typography, type InputRef } from 'antd';
import { ApartmentOutlined, FunnelPlotOutlined, SearchOutlined, UserOutlined } from '@ant-design/icons';
import type { ContactListItem, DealItem, OrganizationListItem, Paginated } from '@crm/shared';
import { api } from '../api/client';
import { useFeatures } from '../features/settings/api';

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);

function useDebounced<T>(value: T, ms: number) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = window.setTimeout(() => setV(value), ms);
    return () => window.clearTimeout(t);
  }, [value, ms]);
  return v;
}

const contactName = (c: { firstName: string | null; lastName: string | null; email: string | null }) =>
  [c.firstName, c.lastName].filter(Boolean).join(' ') || c.email || 'Unnamed contact';

function Option({ icon, title, sub }: { icon: React.ReactNode; title: string; sub?: string | null }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
      <span style={{ color: '#6B7280', flex: 'none' }}>{icon}</span>
      <div style={{ minWidth: 0 }}>
        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{title}</div>
        {sub && <Typography.Text type="secondary" style={{ fontSize: 12 }} ellipsis>{sub}</Typography.Text>}
      </div>
    </div>
  );
}

/** Header search across contacts, organizations and deals. Cmd/Ctrl+K focuses it. */
export function GlobalSearch() {
  const navigate = useNavigate();
  const inputRef = useRef<InputRef>(null);
  const [text, setText] = useState('');
  const q = useDebounced(text.trim(), 250);
  const enabled = q.length >= 2;
  const dealsOn = useFeatures().enabled('pipelines');

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const results = useQuery({
    queryKey: ['global-search', q, dealsOn],
    enabled,
    staleTime: 30_000,
    queryFn: async () => {
      const params = { q, page: 1, pageSize: 5 };
      const [contacts, orgs, deals] = await Promise.all([
        api.get<Paginated<ContactListItem>>('/contacts', { params }),
        api.get<Paginated<OrganizationListItem>>('/organizations', { params }),
        dealsOn ? api.get<Paginated<DealItem>>('/deals', { params: { ...params, status: 'all' } }) : null,
      ]);
      return { contacts: contacts.data.items, orgs: orgs.data.items, deals: deals?.data.items ?? [] };
    },
  });

  const d = results.data;
  const options = !enabled || !d
    ? []
    : [
        d.contacts.length && {
          label: 'Contacts',
          options: d.contacts.map((c) => ({ value: `/contacts/${c.id}`, label: <Option icon={<UserOutlined />} title={contactName(c)} sub={[c.email, c.organization?.name].filter(Boolean).join(' · ')} /> })),
        },
        d.orgs.length && {
          label: 'Organizations',
          options: d.orgs.map((o) => ({ value: `/organizations/${o.id}`, label: <Option icon={<ApartmentOutlined />} title={o.name} sub={o.domain} /> })),
        },
        d.deals.length && {
          label: 'Deals',
          options: d.deals.map((x) => ({ value: `/deals/${x.id}`, label: <Option icon={<FunnelPlotOutlined />} title={x.title} sub={[x.stage.name, x.organization?.name].filter(Boolean).join(' · ')} /> })),
        },
      ].filter(Boolean) as { label: string; options: { value: string; label: React.ReactNode }[] }[];

  return (
    <AutoComplete
      value={text}
      options={options}
      onChange={setText}
      onSelect={(path: string) => {
        setText('');
        inputRef.current?.blur();
        navigate(path);
      }}
      popupMatchSelectWidth={420}
      listHeight={480}
      style={{ width: '100%', maxWidth: 440 }}
      notFoundContent={enabled ? (results.isFetching ? <Spin size="small" /> : results.isError ? 'Search is unavailable right now' : `No matches for “${q}”`) : null}
    >
      <Input
        ref={inputRef}
        variant="filled"
        size="large"
        aria-label={dealsOn ? 'Search contacts, organizations and deals' : 'Search contacts and organizations'}
        placeholder={dealsOn ? 'Search contacts, organizations, deals' : 'Search contacts, organizations'}
        prefix={<SearchOutlined style={{ color: '#9CA3AF', marginRight: 4 }} />}
        suffix={<Typography.Text keyboard type="secondary" style={{ fontSize: 12 }}>{isMac ? '⌘ K' : 'Ctrl K'}</Typography.Text>}
        allowClear={false}
      />
    </AutoComplete>
  );
}
